import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { createLenis } from "../../lenis";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import type { JoinLayoutProps } from "../types";
import { WeddingProgram } from "../WeddingProgram";
import {
  type ApparitionConfig,
  type ApparitionKeyframe,
  APPARITION_DEFAULTS,
} from "@wedding/shared";
import { makeTokenContext, resolveTokens } from "@wedding/shared";

gsap.registerPlugin(ScrollTrigger);

interface ApparitionLayoutProps extends JoinLayoutProps {
  config?: ApparitionConfig;
}

// Linear interpolation between sorted keyframes by progress 0..1.
function interpolateKeyframes(keyframes: ApparitionKeyframe[], progress: number) {
  if (keyframes.length === 0) {
    return { x: 0, y: 0, scale: 1, rotate: 0, opacity: 0 };
  }
  const sorted = [...keyframes].sort((a, b) => a.at - b.at);
  if (progress <= sorted[0].at) return sorted[0];
  const last = sorted[sorted.length - 1];
  if (progress >= last.at) return last;
  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i];
    const b = sorted[i + 1];
    if (progress >= a.at && progress <= b.at) {
      const span = b.at - a.at;
      const t = span === 0 ? 0 : (progress - a.at) / span;
      return {
        at: progress,
        x: a.x + (b.x - a.x) * t,
        y: a.y + (b.y - a.y) * t,
        scale: a.scale + (b.scale - a.scale) * t,
        rotate: a.rotate + (b.rotate - a.rotate) * t,
        opacity: a.opacity + (b.opacity - a.opacity) * t,
      };
    }
  }
  return last;
}

export function ApparitionLayout({
  formSlot,
  program,
  title,
  dateTime,
  location,
  description,
  galleryImages,
  config,
}: ApparitionLayoutProps) {
  const cfg = config ?? APPARITION_DEFAULTS;
  const lenisRef = useRef<Lenis | null>(null);
  const characterRef = useRef<HTMLDivElement>(null);

  const ctx = makeTokenContext({ title, dateTime, location });
  const t = (s: string) => resolveTokens(s.replace("{description}", description ?? ""), ctx);

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = createLenis({ duration: 1.4, easing: (x) => 1 - Math.pow(1 - x, 5) });
    lenis.scrollTo(0, { immediate: true });
    lenisRef.current = lenis;
    const ticker = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(ticker);
    gsap.ticker.lagSmoothing(0);
    lenis.on("scroll", ScrollTrigger.update);

    const gctx = gsap.context(() => {
      // Character follows scroll progress through keyframe path.
      ScrollTrigger.create({
        trigger: "main",
        start: "top top",
        end: "bottom bottom",
        onUpdate(self) {
          const k = interpolateKeyframes(cfg.character.keyframes, self.progress);
          const el = characterRef.current;
          if (!el) return;
          el.style.transform = `translate(${k.x}vw, ${k.y}vh) scale(${k.scale}) rotate(${k.rotate}deg)`;
          el.style.opacity = `${k.opacity}`;
        },
      });

      // Section reveals
      gsap.from(".apr-hero-label", { opacity: 0, y: 20, duration: 1.4, ease: "expo.out" });
      gsap.from(".apr-hero-title", { opacity: 0, y: 60, duration: 1.6, ease: "expo.out", delay: 0.2 });
      gsap.from(".apr-hero-tag", { opacity: 0, y: 40, duration: 1.4, ease: "expo.out", delay: 0.6 });

      gsap.from(".apr-story-line", {
        opacity: 0,
        y: 40,
        stagger: 0.1,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: { trigger: ".apr-story", start: "top 70%", toggleActions: "play reverse play reverse" },
      });

      gsap.from(".apr-date-number", {
        opacity: 0,
        scale: 0.85,
        duration: 1.4,
        ease: "expo.out",
        scrollTrigger: { trigger: ".apr-date", start: "top 70%", toggleActions: "play reverse play reverse" },
      });

      gsap.from(".apr-venue-img", {
        clipPath: "inset(0 100% 0 0)",
        duration: 1.6,
        ease: "expo.inOut",
        scrollTrigger: { trigger: ".apr-venue", start: "top 70%", toggleActions: "play reverse play reverse" },
      });
    });

    return () => {
      lenis.destroy();
      gsap.ticker.remove(ticker);
      ScrollTrigger.killAll();
      gctx.revert();
    };
  }, [cfg]);

  const d = dateTime ? new Date(dateTime) : null;
  const storyLines = (description ?? "").split(/(?<=[.!?])\s+/).filter(Boolean);

  return (
    <main
      className="relative overflow-x-hidden"
      style={{
        background: cfg.colors.background,
        color: cfg.colors.text,
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      {/* ═══ FOREGROUND TRANSPARENT CHARACTER ═══════════════════════════ */}
      <div
        ref={characterRef}
        className="fixed top-0 left-0 z-30 pointer-events-none"
        style={{
          width: "30vw",
          height: "60vh",
          willChange: "transform, opacity",
        }}
      >
        {(cfg.character.hevcUrl || cfg.character.webmUrl) ? (
          <video
            className="w-full h-full object-contain"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
          >
            {/* HEVC first — Safari picks alpha-aware MP4. */}
            {cfg.character.hevcUrl && (
              <source src={cfg.character.hevcUrl} type='video/mp4; codecs="hvc1"' />
            )}
            {cfg.character.webmUrl && (
              <source src={cfg.character.webmUrl} type="video/webm" />
            )}
          </video>
        ) : (
          /* Placeholder when no character video is uploaded yet — a soft floating glyph. */
          <div className="w-full h-full flex items-center justify-center">
            <div
              className="text-[12rem] leading-none opacity-40"
              style={{ color: cfg.colors.accent, fontFamily: "'Playfair Display', serif" }}
            >
              ✦
            </div>
          </div>
        )}
      </div>

      {/* ═══ HERO ═══════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <p
          className="apr-hero-label text-[10px] uppercase tracking-[0.5em] mb-8 opacity-60"
          style={{ color: cfg.colors.accent }}
        >
          {t(cfg.hero.label)}
        </p>
        <h1
          className="apr-hero-title font-light leading-[0.95] tracking-[-0.03em] mb-10 whitespace-pre-line"
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: "clamp(3.5rem, 12vw, 11rem)",
          }}
        >
          {title}
        </h1>
        <p className="apr-hero-tag text-base sm:text-lg tracking-wide opacity-70 max-w-md">
          {t(cfg.hero.tagline)}
        </p>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-60">
          <span className="text-[10px] tracking-[0.4em] uppercase">Scroll</span>
          <div className="w-px h-10" style={{ background: `linear-gradient(to bottom, ${cfg.colors.accent}, transparent)` }} />
        </div>
      </section>

      {/* ═══ STORY ═════════════════════════════════════════════════════ */}
      <section className="apr-story relative px-6 sm:px-16 py-32">
        <div className="max-w-3xl mx-auto">
          <p
            className="apr-story-line text-[10px] uppercase tracking-[0.5em] mb-10"
            style={{ color: cfg.colors.accent }}
          >
            {t(cfg.story.label)}
          </p>
          <h2
            className="apr-story-line font-light leading-[1.05] tracking-tight mb-14 whitespace-pre-line"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.5rem, 6vw, 5rem)" }}
          >
            {t(cfg.story.headline)}
          </h2>
          {storyLines.length > 0 ? (
            storyLines.map((line, i) => (
              <p key={i} className="apr-story-line text-lg leading-relaxed opacity-75 mb-5 max-w-2xl">
                {line.trim()}
              </p>
            ))
          ) : (
            <p className="apr-story-line text-lg leading-relaxed opacity-75 max-w-2xl">
              {t(cfg.story.body)}
            </p>
          )}
        </div>
      </section>

      {/* ═══ DATE ══════════════════════════════════════════════════════ */}
      <section className="apr-date relative px-6 sm:px-16 py-32 text-center">
        <p
          className="text-[10px] uppercase tracking-[0.6em] mb-8"
          style={{ color: cfg.colors.accent }}
        >
          {t(cfg.date.label)}
        </p>
        <div
          className="apr-date-number font-light leading-none tabular-nums tracking-[-0.05em]"
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "clamp(5rem, 18vw, 16rem)",
          }}
        >
          {ctx.dayMonth}
        </div>
        <p
          className="font-light tracking-widest opacity-60 mt-3"
          style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.5rem, 4vw, 3rem)" }}
        >
          {ctx.year}
        </p>
        <p className="text-sm tracking-[0.3em] uppercase opacity-50 mt-10">
          {d?.toLocaleDateString("tr-TR", { weekday: "long" })} · {ctx.time}
        </p>
      </section>

      {/* ═══ VENUE ═════════════════════════════════════════════════════ */}
      <section className="apr-venue relative min-h-screen overflow-hidden">
        {galleryImages[0] && (
          <div className="apr-venue-img absolute inset-0">
            <img
              src={galleryImages[0]}
              alt=""
              className="w-full h-full object-cover"
              style={{ filter: "grayscale(0.6) contrast(1.05) brightness(0.7)" }}
            />
          </div>
        )}
        <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${cfg.colors.background}, transparent 50%, ${cfg.colors.background}66)` }} />
        <div className="relative z-10 px-6 sm:px-16 py-32 flex flex-col justify-end min-h-screen max-w-3xl">
          <p className="text-[10px] uppercase tracking-[0.5em] mb-4" style={{ color: cfg.colors.accent }}>
            {t(cfg.venue.label)}
          </p>
          <h2
            className="font-light leading-tight tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.5rem, 7vw, 6rem)" }}
          >
            {location ?? "—"}
          </h2>
        </div>
      </section>

      {/* ═══ RSVP ══════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex items-center justify-center px-6 py-32">
        <div className="w-full max-w-md text-center">
          <p className="text-[10px] uppercase tracking-[0.5em] mb-3" style={{ color: cfg.colors.accent }}>
            {t(cfg.rsvp.label)}
          </p>
          <h2
            className="font-light leading-tight mb-10"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3.5rem)" }}
          >
            {t(cfg.rsvp.heading)}
          </h2>
          <WeddingProgram entries={program} className="mb-10" />
          {formSlot}
          <p className="mt-10 text-[10px] tracking-[0.4em] uppercase opacity-50">
            {t(cfg.rsvp.closingPrefix)}
          </p>
        </div>
      </section>
    </main>
  );
}
